import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Link
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const BulkUploadModal = ({ open, onClose, title, onUpload, entityRequired, entities = [], optionsForTemplate = {}, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [entityId, setEntityId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(xls|xlsx)$/i)) {
        setError('Please upload a valid Excel file (.xlsx or .xls).');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    if (entityRequired && !entityId) {
      setError('Please select a legal entity.');
      return;
    }

    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);
    if (entityRequired) {
      formData.append('entityId', entityId);
    }

    try {
      const response = await onUpload(formData);

      const responseData = response?.data || response;
      const results = responseData?.results || [];
      const errors = responseData?.errors || [];

      if (errors.length > 0) {
        if (results.length > 0) {
          setError(`Partial Success: Uploaded ${results.length} rows. Errors:\n` + errors.join('\n'));
        } else {
          setError('Upload Failed:\n' + errors.join('\n'));
        }

        // Still call onSuccess if at least some rows were uploaded
        if (results.length > 0 && onSuccess) {
          onSuccess(response);
        }
      } else {
        setFile(null);
        setEntityId('');
        if (onSuccess) onSuccess(response);
        else onClose(); // Fallback if onSuccess is not provided but we want to close
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setEntityId('');
    setError('');
    onClose();
  };

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Template');

    let headers = [];
    let sampleData = [];
    let fileName = 'Template.xlsx';

    if (title.includes('Designation')) {
      headers = ['Name', 'Code', 'Description', 'Status'];
      sampleData = [
        ['Software Engineer', 'SE-01', 'Develops and maintains software applications', 'Active'],
        ['Product Manager', 'PM-01', 'Leads product strategy and execution', 'Active']
      ];
      fileName = 'Designations_Template.xlsx';

      worksheet.addRow(headers);
      sampleData.forEach(row => worksheet.addRow(row));

      // Data Validation for Status
      for (let i = 2; i <= 1000; i++) {
        worksheet.getCell(`D${i}`).dataValidation = {
          type: 'list', allowBlank: true, formulae: ['"Active,Inactive"']
        };
      }
    } else if (title.includes('Department')) {
      headers = ['Name', 'Description'];
      sampleData = [
        ['Engineering', 'Responsible for core product development'],
        ['Human Resources', 'Manages employee relations and recruitment']
      ];
      fileName = 'Departments_Template.xlsx';

      worksheet.addRow(headers);
      sampleData.forEach(row => worksheet.addRow(row));
    } else if (title.includes('Location')) {
      headers = ['Name', 'Address1', 'Address2', 'City', 'State', 'ZipCode', 'Country', 'Timezone', 'Description'];
      sampleData = [
        ['Headquarters', '123 Main St', 'Suite 100', 'San Francisco', 'CA', '94105', 'USA', 'PST', 'Main corporate office'],
        ['London Branch', '45 Oxford St', '', 'London', 'London', 'W1D 2DZ', 'UK', 'GMT', 'European regional office']
      ];
      fileName = 'Locations_Template.xlsx';

      worksheet.addRow(headers);
      sampleData.forEach(row => worksheet.addRow(row));
    } else if (title.includes('Employee')) {
      headers = [
        'First Name', 'Last Name', 'Email', 'Mobile Number',
        'Entity', 'Department', 'System Role', 'Designation',
        'Location', 'Manager Email', 'Employment Type', 'Fresher',
        'Existing PF', 'Collect 12BB', 'Collect ESIC'
      ];
      sampleData = [
        [
          'John', 'Doe', 'john.doe@example.com', '123678990',
          optionsForTemplate.entities?.[0] || 'Acme Corp',
          optionsForTemplate.departments?.[0] || 'Engineering',
          optionsForTemplate.roles?.[0] || 'Employee',
          optionsForTemplate.designations?.[0] || 'Software Engineer',
          'Headquarters', 'ohn.doe@example.com', 'Full-time', 'No',
          'No', 'No', 'No'
        ]
      ];
      fileName = 'Employees_Template.xlsx';

      worksheet.addRow(headers);
      sampleData.forEach(row => worksheet.addRow(row));

      // Add Dropdowns for categorical fields
      // E=Entity, F=Department, G=Role, H=Designation, K=Employment Type, L=Fresher, M=Existing PF, N=Collect 12BB, O=Collect ESIC
      const formatList = (arr) => arr && arr.length > 0 ? `"${arr.map(x => x.replace(/,/g, '')).join(',')}"` : '""';

      for (let i = 2; i <= 1000; i++) {
        if (optionsForTemplate.entities?.length) {
          worksheet.getCell(`E${i}`).dataValidation = { type: 'list', allowBlank: true, formulae: [formatList(optionsForTemplate.entities)] };
        }
        if (optionsForTemplate.departments?.length) {
          worksheet.getCell(`F${i}`).dataValidation = { type: 'list', allowBlank: true, formulae: [formatList(optionsForTemplate.departments)] };
        }
        if (optionsForTemplate.roles?.length) {
          worksheet.getCell(`G${i}`).dataValidation = { type: 'list', allowBlank: true, formulae: [formatList(optionsForTemplate.roles)] };
        }
        if (optionsForTemplate.designations?.length) {
          worksheet.getCell(`H${i}`).dataValidation = { type: 'list', allowBlank: true, formulae: [formatList(optionsForTemplate.designations)] };
        }
        worksheet.getCell(`K${i}`).dataValidation = { type: 'list', allowBlank: true, formulae: ['"Full-time,Part-time,Contract,Intern,Consultant"'] };
        worksheet.getCell(`L${i}`).dataValidation = { type: 'list', allowBlank: true, formulae: ['"Yes,No"'] };
        worksheet.getCell(`M${i}`).dataValidation = { type: 'list', allowBlank: true, formulae: ['"Yes,No"'] };
        worksheet.getCell(`N${i}`).dataValidation = { type: 'list', allowBlank: true, formulae: ['"Yes,No"'] };
        worksheet.getCell(`O${i}`).dataValidation = { type: 'list', allowBlank: true, formulae: ['"Yes,No"'] };
      }

    } else {
      headers = ['Name', 'Description'];
      sampleData = [['Sample Name', 'Sample Description']];
      worksheet.addRow(headers);
      sampleData.forEach(row => worksheet.addRow(row));
    }

    // Auto-fit columns
    worksheet.columns.forEach(column => {
      column.width = 20; // Default width
    });

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), fileName);
  };

  return (
    <Dialog open={open} onClose={!loading ? handleClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, color: '#030007' }}>
        {title || 'Bulk Upload'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity={error.includes('Partial Success') ? 'warning' : 'error'} sx={{ mb: 2, mt: 1, whiteSpace: 'pre-line' }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Please upload an Excel (.xlsx, .xls) file. Ensure the first row contains the column headers.
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={handleDownloadTemplate}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: '#7522D1',
              textDecoration: 'none',
              mb: 3,
              fontWeight: 500,
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            <DownloadIcon sx={{ fontSize: 18, mr: 0.5 }} />
            Download Sample Excel Template
          </Link>

          {entityRequired && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Legal Entity *</InputLabel>
              <Select
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                label="Legal Entity *"
              >
                <MenuItem value=""><em>Select Entity</em></MenuItem>
                {entities.map((entity) => (
                  <MenuItem key={entity.id} value={entity.id}>
                    {entity.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            fullWidth
            sx={{
              padding: 3,
              borderStyle: 'dashed',
              borderWidth: 2,
              borderColor: '#ccc',
              color: '#4F4937',
              textTransform: 'none',
              '&:hover': {
                borderStyle: 'dashed',
                borderWidth: 2,
                borderColor: '#7522D1',
                backgroundColor: 'rgba(117, 34, 209, 0.04)',
              }
            }}
          >
            {file ? file.name : 'Click or Drag to Upload Excel File'}
            <input
              type="file"
              hidden
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={handleFileChange}
            />
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ color: '#030007', textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={loading || !file || (entityRequired && !entityId)}
          sx={{
            backgroundColor: '#7522D1',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#5E1BA8' },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadModal;
